import { useEffect, useRef, useState } from "react";
import { X, Link2, Link2Off } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { mapPath } from "./routes";

// Vorschau „Nebeneinander“: links Version 1, rechts Version 2, beide in Desktop Breite verkleinert.
// Seitenwechsel in einer Hälfte zieht die andere mit, Scrollen läuft auf Wunsch anteilig gekoppelt.

const VIRTUAL_W = 1280;
const HASH = process.env.REACT_APP_HASH_ROUTER === "1";
const LABELS = {
    de: { 1: "Version 1 · Original neu gestaltet", 2: "Version 2 · Einfaches Deutsch", sync: "Scrollen gekoppelt", unsync: "Scrollen getrennt", close: "Nebeneinander beenden" },
    en: { 1: "Version 1 · Original redesigned", 2: "Version 2 · Plain language", sync: "Scroll linked", unsync: "Scroll separate", close: "Close side by side" },
};
const norm = (p) => (p && p.length > 1 ? p.replace(/\/$/, "") : p || "/");

const srcFor = (route, v, rounded) => {
    const q = `?v=${v}&embed=1&r=${rounded ? 1 : 0}`;
    return HASH ? `${window.location.origin}${window.location.pathname}${q}#${route}` : `${window.location.origin}${route}${q}`;
};

const Pane = ({ v, label, src, frameRef }) => {
    const box = useRef(null);
    const [size, setSize] = useState({ w: 600, h: 600 });
    useEffect(() => {
        const el = box.current;
        if (!el) return undefined;
        const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const scale = size.w / VIRTUAL_W;
    return (
        <section className="pv-pane" aria-label={label}>
            <header className="pv-pane-head"><span className="pv-pane-badge">{v}</span>{label}</header>
            <div ref={box} className="pv-pane-body">
                <iframe
                    ref={frameRef}
                    title={label}
                    src={src}
                    style={{ width: VIRTUAL_W, height: size.h / scale, transform: `scale(${scale})` }}
                />
            </div>
        </section>
    );
};

const SideBySide = ({ startPath, rounded, onClose }) => {
    const { lang } = useLanguage();
    const L = LABELS[lang === "en" ? "en" : "de"];
    const f1 = useRef(null);
    const f2 = useRef(null);
    const at = useRef({ 1: mapPath(startPath, "1"), 2: mapPath(startPath, "2") });
    const pending = useRef({ 1: null, 2: null });
    const [srcs] = useState(() => ({ 1: srcFor(at.current[1], "1", rounded), 2: srcFor(at.current[2], "2", rounded) }));
    const [linked, setLinked] = useState(true);
    const linkedRef = useRef(true);
    linkedRef.current = linked;

    useEffect(() => {
        const frames = { 1: f1, 2: f2 };
        const post = (v, msg) => frames[v].current?.contentWindow?.postMessage({ src: "parto-host", ...msg }, "*");
        const onMessage = (e) => {
            const d = e.data;
            if (!d || d.src !== "parto-embed") return;
            const from = e.source === f1.current?.contentWindow ? "1" : e.source === f2.current?.contentWindow ? "2" : null;
            if (!from) return;
            const other = from === "1" ? "2" : "1";
            if (d.type === "nav") {
                at.current[from] = d.path;
                // Folge eines eigenen „go“: nur merken, nicht zurückspiegeln
                if (pending.current[from] && norm(pending.current[from]) === norm(d.path)) { pending.current[from] = null; return; }
                pending.current[from] = null;
                const target = mapPath(d.path, other);
                if (norm(target) !== norm(at.current[other])) { at.current[other] = target; pending.current[other] = target; post(other, { type: "go", path: target }); }
            }
            if (d.type === "esc") onClose(at.current);
            if (d.type === "scroll" && linkedRef.current) post(other, { type: "scroll", ratio: d.ratio });
        };
        const onKey = (e) => e.key === "Escape" && onClose(at.current);
        window.addEventListener("message", onMessage);
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { window.removeEventListener("message", onMessage); window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    }, [onClose]);

    return (
        <div className="pv-sbs" role="dialog" aria-modal="true" aria-label="Nebeneinander">
            <div className="pv-sbs-bar">
                <button type="button" className={`pv-chip ${linked ? "is-on" : ""}`} onClick={() => setLinked((x) => !x)} aria-pressed={linked}>
                    {linked ? <Link2 size={14} /> : <Link2Off size={14} />} {linked ? L.sync : L.unsync}
                </button>
                <button type="button" className="pv-chip" onClick={() => onClose(at.current)}><X size={14} /> {L.close}</button>
            </div>
            <div className="pv-sbs-grid">
                <Pane v="1" label={L[1]} src={srcs[1]} frameRef={f1} />
                <Pane v="2" label={L[2]} src={srcs[2]} frameRef={f2} />
            </div>
        </div>
    );
};

export default SideBySide;
